import { track } from "./analytics";
import { pageNameFor } from "./pageNames";

// The product-facing analytics surface: components import these typed domain
// functions, never track()/mixpanel directly. Event names are static literals
// in snake_case object_verb form — docs/analytics.md is the taxonomy of record.

export type AnalyticsEventName =
  | "user_signed_in"
  | "page_viewed"
  | "comment_created"
  | "comment_resolved"
  | "comment_unresolved"
  | "indicator_score_overridden"
  | "indicator_score_override_removed"
  | "finding_added"
  | "report_export_requested"
  | "report_export_completed"
  | "report_notes_expanded";

// Unlike admin/utils reportId(), this never throws off report pages — analytics
// helpers must be safe to call from any URL.
export function reportIdFromPath(): string | null {
  const match = window.location.pathname.match(/\/reports\/(\d+)/);
  return match ? match[1] : null;
}

// Resolves the commentable's model name from Apollo's __typename when present,
// else by decoding the graphql-ruby gid (base64 of "TypeName-id").
export function contentTypeOf(object: { __typename?: string, gid?: string }): string | null {
  if (object.__typename) return object.__typename;
  if (!object.gid) return null;
  try {
    return atob(object.gid).split("-")[0] || null;
  } catch {
    return null;
  }
}

export function trackSignedIn({ method }: { method: string }): void {
  track("user_signed_in", { method });
}

const PAGEVIEW_DEDUPE_KEY = "corsis_last_page_viewed";
// The PubSub "navigate" handler in AdminRouter both reloads and pushes history,
// so the same URL can be tracked once before and once after a full page load.
// sessionStorage (unlike module state) survives the reload and absorbs the dupe.
const PAGEVIEW_DEDUPE_WINDOW_MS = 5000;

export function trackPageView(): void {
  const url = window.location.pathname + window.location.search;
  try {
    const raw = sessionStorage.getItem(PAGEVIEW_DEDUPE_KEY);
    if (raw) {
      const last = JSON.parse(raw);
      if (last.url === url && Date.now() - last.ts < PAGEVIEW_DEDUPE_WINDOW_MS) return;
    }
    sessionStorage.setItem(PAGEVIEW_DEDUPE_KEY, JSON.stringify({ url, ts: Date.now() }));
  } catch { }
  track("page_viewed", {
    page_name: pageNameFor(window.location.pathname),
    path: window.location.pathname
  });
}

export function trackCommentCreated(props: {
  report_id: string | number | null
  content_type: string | null
  commentable_gid: string
  field: string
  comment_length: number
}): void {
  track("comment_created", props);
}

export function trackCommentResolved(props: {
  report_id: string | number | null
  comment_gid: string
  content_type: string | null
}): void {
  track("comment_resolved", props);
}

export function trackCommentUnresolved(props: {
  report_id: string | number | null
  comment_gid: string
  content_type: string | null
}): void {
  track("comment_unresolved", props);
}

export function trackIndicatorScoreOverridden(props: {
  report_id: string | number | null
  indicator_gid: string
  reason_code_id?: string | number | null
  calculated_score?: number | null
  override_score?: number | null
}): void {
  track("indicator_score_overridden", props);
}

export function trackIndicatorScoreOverrideRemoved(props: {
  report_id: string | number | null
  indicator_gid: string
}): void {
  track("indicator_score_override_removed", props);
}

export function trackFindingAdded(props: {
  report_id: string | number | null
  source: "preset" | "new"
  finding_id?: string | number | null
  finding_type?: string | null
  source_type?: string | null
}): void {
  track("finding_added", props);
}

export function trackReportExportRequested(props: {
  report_id: string | number | null
  channel: string
  format?: string | null
  build_id?: string | number | null
}): void {
  track("report_export_requested", props);
}

export function trackReportExportCompleted(props: {
  report_id: string | number | null
  channel: string
  status: "success" | "failed"
  format?: string | null
  build_id?: string | number | null
}): void {
  track("report_export_completed", props);
}

export function trackReportNotesExpanded(props: { report_id: string | number | null }): void {
  track("report_notes_expanded", props);
}
